import { useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import ReactPlayer from 'react-player/vimeo'
import { PlayIcon } from '@velasca/icons/play'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import { handleError } from '@velasca/utils/handle-error'
import { setQueryParams } from '@velasca/utils/set-query-params'
import {
  ClickArea,
  ReactPlayerWrapper,
  VideoBlock,
  VideoPlayerWithCover,
  VideoPreviewImage,
} from './CustomVideoPlayer.styles'
import type { CustomVideoPlayerProps } from './CustomVideoPlayer.types'

export const CustomVideoPlayer = ({
  image,
  src,
  isMobile = false,
  onClick,
  className = '',
}: CustomVideoPlayerProps): JSX.Element => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const [isPreviewOn, setIsPreviewOn] = useState(true)
  const [ref, inView] = useInView()

  const handleVideoReset = () => {
    setIsVideoPlaying(false)
    setIsPreviewOn(true)
  }

  const handleVideoPause = () => {
    setIsVideoPlaying(false)
  }

  const handleVideoPlay = () => {
    setIsVideoPlaying(true)
  }

  const handleClick = () => {
    if (!src) {
      return
    }

    if (!isMobile) {
      onClick?.()
    }
  }

  const startVideo = () => {
    setIsVideoPlaying(true)
    setIsPreviewOn(false)
  }

  useEffect(() => {
    if (inView) {
      return
    }

    setIsVideoPlaying(false)
  }, [inView])

  return (
    <VideoBlock
      ref={ref}
      isMobile={isMobile}
      hasVideo={Boolean(src)}
      className={className}
      onClick={handleClick}
    >
      {isMobile ? (
        Boolean(src) && (
          <VideoPlayerWithCover>
            <ReactPlayerWrapper isPreviewOn={isPreviewOn}>
              <ReactPlayer
                controls
                playsinline
                playing={isVideoPlaying}
                url={src}
                width="100%"
                height="100%"
                onEnded={handleVideoReset}
                onError={handleError}
                onPause={handleVideoPause}
                onPlay={handleVideoPlay}
              />
            </ReactPlayerWrapper>
            {isPreviewOn && (
              <>
                <VideoPreviewImage
                  lqip
                  lazyload
                  alt={image.alt}
                  sources={buildIntrinsicWidthSources({
                    src: image.src,
                    maxWidth: image.width,
                  })}
                  imgSrc={setQueryParams(image.src, [
                    { name: 'width', value: '30' },
                  ])}
                />
                <ClickArea onClick={startVideo}>
                  <PlayIcon width="50" height="50" />
                </ClickArea>
              </>
            )}
          </VideoPlayerWithCover>
        )
      ) : (
        <>
          {Boolean(src) && (
            <ClickArea onClick={startVideo}>
              <PlayIcon width="50" height="50" />
            </ClickArea>
          )}
          <VideoPreviewImage
            lqip
            lazyload
            alt={image.alt}
            imgSrc={setQueryParams(image.src, [{ name: 'width', value: '30' }])}
            sources={buildIntrinsicWidthSources({
              src: image.src,
              maxWidth: image.width,
            })}
          />
        </>
      )}
    </VideoBlock>
  )
}
